import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Flex from '@components/Flex/Flex';
import Text from '@components/Text/Text';
import Avatar from '@components/Avatar/Avatar';
import { useTheme } from '@mui/material';
import { useAppSelector } from '@app/hooks';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { useState } from 'react';
import { Profile, Logout } from '@assets/svg';
import { useNavigate } from 'react-router-dom';
import { MenuItemText, StyledMenu, StyledMenuItem } from './ProfileMenu.styles';
import { User } from '@features/api/response/User';
import LogoutConfirmation from '@components/LogoutConfirmation/LogoutConfirmation';

export interface ProfileMenuProps {
  user?: User;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
  user,
}: ProfileMenuProps) => {
  const theme = useTheme();
  const lastLogin = useAppSelector((state) => state.auth.lastLogin);
  const navigate = useNavigate();

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseLogoutConfirmation = () => {
    handleClose();
    setLogoutDialogOpen(false);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleClose();
  };

  if (!user) return <></>;
  return (
    <>
      <Flex
        data-testid='profile-menu-container'
        alignItems='center'
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <Avatar
          user={{ ...user.basicDetails, avatar: user.avatar }}
          size='medium'
        />
        <Flex marginX={5} flexDirection='column'>
          <Text variant='bodyBig' fontWeight='fontWeightSemiBold'>
            {`${user?.basicDetails.firstName} ${user?.basicDetails.lastName}`}
          </Text>
          <Text
            variant='body'
            fontWeight='fontWeightRegular'
            sx={{ color: theme.palette.grey.A100 }}
          >
            <FormattedMessage
              id='home.lastLogin'
              values={{
                lastLoginDate: moment(lastLogin).format('DD MMM YYYY'),
              }}
            />
          </Text>
        </Flex>
        <KeyboardArrowDownIcon fontSize='small' />
      </Flex>
      <StyledMenu
        data-testid='profile-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        autoFocus={false}
        disableAutoFocus
        disableEnforceFocus
        sx={{ marginTop: theme.spacing(4) }}
      >
        <StyledMenuItem
          data-testid='profile-menu-item'
          onClick={handleProfileClick}
          sx={{
            width: anchorEl && anchorEl.offsetWidth,
          }}
        >
          <Profile width={24} height={24} />
          <MenuItemText variant='h6' fontWeight='fontWeightMedium'>
            <FormattedMessage id='menu.profile' />
          </MenuItemText>
        </StyledMenuItem>
        <StyledMenuItem
          data-testid='logout-menu-item'
          onClick={handleLogoutClick}
          sx={{ width: anchorEl && anchorEl.offsetWidth }}
        >
          <Logout width={24} height={24} />
          <MenuItemText variant='h6' fontWeight='fontWeightMedium'>
            <FormattedMessage id='menu.logout' />
          </MenuItemText>
        </StyledMenuItem>
      </StyledMenu>
      <LogoutConfirmation
        isOpen={logoutDialogOpen}
        onClose={handleCloseLogoutConfirmation}
      />
    </>
  );
};

export default ProfileMenu;
